import { Component, computed, inject, input, signal } from '@angular/core';
import { form, FormField, FormRoot } from '@angular/forms/signals';
import { KeycloakAuthService } from '@dss-libs/shared/data-access/auth';
import { ClientService } from '@dss-libs/shared/data-access/clients';
import { IInviteMembersPayload, UserRole } from '@dss-libs/shared/type/common-types';
import { ButtonComponent, JunoInput, JunoSelect } from '@dss-libs/shared/ui/common-components';
import { TranslocoDirective } from '@jsverse/transloco';
import { PaymentPlansComponent } from '../../payment-plans/payment-plans.component';
import { IMemberRow, IInviteMembersFormModel, inviteMembersSchema } from '@dss-libs/portal/type/portal-types';

const INITIAL_MEMBER_COUNT = 3;

@Component({
  selector: 'dss-libs-feature-invite-members',
  imports: [TranslocoDirective, FormField, FormRoot, JunoInput, JunoSelect, PaymentPlansComponent, ButtonComponent],
  templateUrl: './invite-members.component.html',
  styleUrl: './invite-members.component.css',
})
export class InviteMembersComponent {
  readonly #clientService = inject(ClientService);
  readonly #keycloakAuthService = inject(KeycloakAuthService);

  protected readonly planId = input<string>();

  protected readonly roles = [
    { value: UserRole.User, label: 'User' },
    { value: UserRole.Admin, label: 'Admin' },
  ];

  protected readonly trustBadges = ['EIDAS QES', 'GDPR', 'SOC 2 II', 'SSL'] as const;

  protected readonly membersModel = signal<IInviteMembersFormModel>({
    members: Array.from({ length: INITIAL_MEMBER_COUNT }, () => this.#createEmptyMember()),
  });

  protected readonly membersForm = form(this.membersModel, inviteMembersSchema, {
    submission: {
      action: async (field) => {
        const payload = this.#createInvitePayload(field().value().members);
        this.#clientService.saveInvitedEmailsLocally(payload);
        this.#openSubscriptionModal();
      },
    },
  });

  protected readonly hasAnyValue = computed(() =>
    this.membersModel().members.some(({ email }) => email.trim().length > 0)
  );

  protected readonly hasEmptyFields = computed(() =>
    this.membersModel().members.some(({ email }) => email.trim().length === 0)
  );

  protected readonly isOnlyOneMemberLeft = computed(() => this.membersModel().members.length <= 1);

  protected readonly isSubmitDisabled = computed(
    () => this.membersForm().invalid() || this.membersForm().submitting() || !this.hasAnyValue()
  );

  protected addNewMember(): void {
    this.membersForm.members().value.update((members) => [...members, this.#createEmptyMember()]);
  }

  protected removeMember(index: number): void {
    this.membersForm.members().value.update((members) => members.filter((_, memberIndex) => memberIndex !== index));
  }

  protected skipInvitingMembers(): void {
    this.#clientService.deleteEmailsSavedLocally();
    this.#openSubscriptionModal();
  }

  protected goBack(): void {
    void this.#keycloakAuthService.logout();
  }

  #createInvitePayload(members: IMemberRow[]): IInviteMembersPayload[] {
    return members.filter(({ email }) => email.trim().length > 0);
  }

  #createEmptyMember(): IMemberRow {
    return {
      email: '',
      role: UserRole.User,
    };
  }

  #openSubscriptionModal(): void {
    this.#clientService.showSubscriptionModal.set(true);
  }
}
